import React from 'react';
import {
  Card, CardSkeleton, ICON_ALERT_CIRCLE_OUTLINE, ICON_CLIPBOARD_TEXT_OUTLINE, Message,
  Stack,
  SimpleTable,
  SimpleTableSkeleton,
} from '@doveit/bricks';
import { GetFeedbacksFilters } from '../../../providers/api/feedback/feedbackProvider';
import useFeedbacks from '../../hooks/use-feedbacks/useFeedbacks';
import ViewFeedbackTableRow from '../view-feedback-table-row/ViewFeedbackTableRow';
import { delay } from '../../../utils/delay/delay';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import ViewFeedbackCard from '../view-feedback-card/ViewFeedbackCard';
import useRBAC from '../../../hooks/use-rbac/useRBAC';

export interface FeedbacksWidgetProps extends React.AriaAttributes {
  filters?: GetFeedbacksFilters,
  canSeeFeedback?: boolean,
  canUpdateStatus?: boolean,
  onFeedbacksLoaded?: VoidFunction,
  onFeedbackUpdated?: VoidFunction,
}

export const LOAD_FEEDBACKS_ERROR_MESSAGE = 'Non è stato possibile caricare le survey.';
export const NO_FEEDBACKS_MESSAGE = 'Non sono presenti survey.';

const FeedbacksWidget: React.FC<FeedbacksWidgetProps> = ({
  filters,
  canSeeFeedback = false,
  canUpdateStatus = false,
  onFeedbacksLoaded,
  onFeedbackUpdated,
  ...rest
}) => {
  const alreadyCalled = React.useRef(false);
  const isMobile = useIsDevice('mobile');
  const { userIsAdmin, userIsCallCenter } = useRBAC();

  const {
    data: feedbacks,
    isLoading: areFeedbacksLoading,
    error: feedbacksError,
    mutate: mutateFeedbacks,
  } = useFeedbacks(filters, undefined, onFeedbacksLoaded && !alreadyCalled.current ? {
    onSuccess: () => {
      onFeedbacksLoaded();
      alreadyCalled.current = true;
    },
  } : undefined);

  const handleSurveyUpdated = React.useCallback(async () => {
    await delay(1 * 1000);

    onFeedbackUpdated?.();
    mutateFeedbacks();
  }, [mutateFeedbacks, onFeedbackUpdated]);

  const processableFeedbacks = React.useMemo(() => feedbacks?.content.filter((feedback) => feedback.email.sentAt), [feedbacks?.content]);

  const canViewWidget = React.useMemo(() => userIsAdmin || userIsCallCenter, [userIsAdmin, userIsCallCenter]);

  if (areFeedbacksLoading) {
    return (
      <CardSkeleton aria-label="Survey in caricamento">
        <SimpleTableSkeleton />
      </CardSkeleton>
    );
  }

  if (feedbacksError) {
    return (
      <Card>
        <Card.Header
          icon={{ path: ICON_ALERT_CIRCLE_OUTLINE }}
          title="Survey"
          color="critical"
        />
        <Card.Content>
          <Message message={LOAD_FEEDBACKS_ERROR_MESSAGE} />
        </Card.Content>
      </Card>
    );
  }

  return canViewWidget && processableFeedbacks && (
    <Card
      aria-label="Sezione survey"
      {...rest}
    >
      <Card.Header
        title="Survey"
        icon={{ path: ICON_CLIPBOARD_TEXT_OUTLINE }}
      />
      <Card.Content>
        {(!isMobile && processableFeedbacks.length > 0) && (
          <SimpleTable>
            <SimpleTable.Body>
              {processableFeedbacks.map((feedback) => (
                <SimpleTable.Row
                  asChild
                  key={feedback.id}
                >
                  <ViewFeedbackTableRow
                    feedback={feedback}
                    onSurveyUpdated={handleSurveyUpdated}
                    canUpdateStatus={canUpdateStatus}
                    canSeeFeedback={canSeeFeedback}
                  />
                </SimpleTable.Row>
              ))}
            </SimpleTable.Body>
          </SimpleTable>
        )}

        {(isMobile && processableFeedbacks.length > 0) && (
          <Stack gap={150} aria-label="Lista survey">
            {processableFeedbacks.map((feedback) => (
              <ViewFeedbackCard
                key={feedback.id}
                feedback={feedback}
                onSurveyUpdated={handleSurveyUpdated}
                canUpdateStatus={canUpdateStatus}
                canSeeFeedback={canSeeFeedback}
              />
            ))}
          </Stack>
        )}

        {processableFeedbacks.length === 0 && <Message message={NO_FEEDBACKS_MESSAGE} />}
      </Card.Content>
    </Card>
  );
};

export default FeedbacksWidget;
